import Chart from "chart.js";
import React from "react";
import { defaults } from 'react-chartjs-2';
// ie11 event constructor polyfill
import "events-polyfill/event-constructor-polyfill.min.js";

const ticksWithoutMinMax = {
    callback: label => (label).toFixed(label > -1 && label < 1 ? 3 : 2).replace(/\d(?=(\d{3})+\.)/g, '$&,'),
    padding: 10,
    maxTicksLimit: 6,
    precision: 5,
}
const ticksWithMinMax = (minY, maxY) => {
    return {
        callback: label => (label).toFixed(label > -1 && label < 1 ? 3 : 2).replace(/\d(?=(\d{3})+\.)/g, '$&,'),
        padding: 10,
        stepSize: (maxY - minY) / 4,
        maxTicksLimit: 6,
        precision: 5,
        suggestedMax: maxY,
        suggestedMin: minY,
    }
}

// see forsyth barr design systems for available colours
const defaultChartColours = {
    highCliffGreen: '#79b31d',
    victoryBeachBlue: '#1dabf7',
    brightonBlue: '#037cc0',
    silverFernGrey: '#dfe0e3',
    stargazingBlue: '#121d26',
    graphOrange: '#E98E3A',
};

const config = (ticks, currencyValue, state) => {
    return {
        data: {
            labels: [],
            datasets: [{
                backgroundColor: defaultChartColours.highCliffGreen,
                borderColor: defaultChartColours.highCliffGreen,
                data: [],
                fill: false,
                pointRadius: 0,
                pointHoverBackgroundColor: defaultChartColours.highCliffGreen,
                pointHoverBorderColor: defaultChartColours.highCliffGreen,
                pointHoverRadius: 4,
                pointHitRadius: 16,
            }],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            defaultFontFamily: defaults.global.defaultFontFamily = "'Lato'",
            showXLabels: 10,
            title: {
                display: false,
            },
            legend: {
                display: false,
            },
            scales: {
                xAxes: [{
                    gridLines: {
                        display: false,
                    },
                    type: 'time',
                    distribution: 'series',
                    ticks: {
                        source: 'data',
                        autoSkip: true,
                        maxTicksLimit: 12,
                    },
                    time: {
                        unit: 'day',
                        stepSize: 1,
                        displayFormats: {
                            day: 'DD MMM h:mm a',
                            week: 'DD MMM',
                            year: 'MMM YYYY',
                        },
                        tooltipFormat: 'DD MMM YYYY',
                    },
                }],
                yAxes: [{
                    scaleLabel: {
                        display: true,
                        labelString: state && state.yLabel ? state.yLabel.toUpperCase() : '',
                        fontFamily: 'Lato',
                        fontSize: '14',
                        fontStyle: '500',
                        padding: '10'
                    },
                    gridLines: {
                        color: defaultChartColours.silverFernGrey,
                        drawBorder: false,
                        tickMarkLength: 0,
                        zeroLineColor: defaultChartColours.silverFernGrey,
                        zeroLineWidth: 3,
                    },
                    ticks: ticks,
                }],
            },
            tooltips: {
                // omit `yAlign` so tooltip auto adjusts layout
                mode: 'index',
                intersect: false,
                backgroundColor: 'white',
                cornerRadius: 8,
                caretSize: 10,
                caretPadding: 10,
                xPadding: 16,
                yPadding: 12,
                displayColors: false,
                bodyFontColor: defaultChartColours.stargazingBlue,
                bodyFontSize: 14,
                bodyFontStyle: 'bold',
                shadowOffsetX: 0,
                shadowOffsetY: 3,
                shadowBlur: 8,
                shadowColor: 'rgba(0, 0, 0, 0.12)',
                callbacks: {
                    title: () => false,
                    label: (item) => {
                        const { value, label } = item;
                        const formatted = Number(value).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
                        return `Price: ${formatted} ${currencyValue} (${label})`;
                    },
                },
                custom: function(tooltip) {
                    syncTooltip(tooltip);
                },
            },
            hover: {
                mode: 'index',
                intersect: false,
                animationDuration: 0,
            },
            elements: {
                line: {
                    tension: 0,
                },
            },
        },
        plugins: [{
            afterDatasetsDraw: (chart) => {
                if (chart.tooltip._active && chart.tooltip._active.length) {
                    const activePoint = chart.tooltip._active[0];
                    const { ctx } = chart.chart;
                    const yAxis = chart.scales['y-axis-0'];
                    const { x, y } = activePoint.tooltipPosition();
                    const bottomY = yAxis.bottom;

                    // draw line
                    ctx.save();
                    ctx.beginPath();
                    ctx.moveTo(x, y + 7);
                    ctx.lineTo(x, bottomY + 1);
                    ctx.setLineDash([5, 5]);
                    ctx.lineWidth = 2;
                    ctx.strokeStyle = defaultChartColours.victoryBeachBlue;
                    ctx.stroke();
                    ctx.restore();
                }
            },
        }],
    }
};

const chartConfig = (minY , maxY, currency = 'NZD', state = {}) => {
    let ticks = ticksWithoutMinMax;
    if (typeof minY !== 'undefined' && typeof maxY !== 'undefined') {
        ticks = ticksWithMinMax(minY, maxY);
    }

    return config(ticks, currency, state);
};

const chartRange = (currentChartRangeId) => {
    let time = {};
    switch (currentChartRangeId) {
        case 'T':
            time = {
                unit: 'minute',
                tooltipFormat: 'h:mm a',
            };
            break;
        case '5D':
            time = {
                unit: 'day',
                tooltipFormat: 'ddd, DD MMM h:mm a',
            };
            break;
        case '1M':
            time = {
                unit: 'week',
                tooltipFormat: 'DD MMM h:mm a',
            };
            break;
        case '6M':
        case 'YTD':
            time = {
                unit: 'week',
                tooltipFormat: 'DD MMM YYYY',
            };
            break;
        case '1Y':
            time = {
                unit: 'month',
                tooltipFormat: 'DD MMM YYYY',
            };
            break;
        case '5Y':
        case 'Max':
            time = {
                unit: 'year',
                tooltipFormat: 'DD MMM YYYY',
            };
            break;
        default:
            time = {
                unit: 'minute',
                tooltipFormat: 'h:mm a',
            };
            break;
    }

    return time;
}

function ChartError(props)
{
    const {message} = props;
    return (
        <div className="empty d-table text-center w-100">
            <div className="h5 text-uppercase font-weight-bold d-table-cell align-middle"><span className="icon error-data">&nbsp;</span> {message ? message : 'Unable to load graph data'}</div>
        </div>
    );
}

const ChartToolbar = (props) => {
    const { context, extraClasses = '' } = props;
    return (
        <div className={`chart-toolbar ${extraClasses}`}>
            <ul className="nav nav-pills">
                {context?.state?.chartRanges?.map(({ id, label }, key) => {
                    const isActive = id === context?.state?.currentChartRangeId;

                    return (
                        <li className="nav-item" key={key}>
                            <button
                                type="button"
                                className={`nav-link${(isActive ? ' active' : '')}`}
                                data-toggle="chart"
                                onClick={() => {
                                    context.setCurrentChartRange(id);
                                }}
                            >{label}</button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

const chartConfigurator = {
    config: null,

    setup: function (existingConfig) {
        this.config = existingConfig;
    },

    // sync tooltips / pointer across multiple charts on the same page
    sync: function () {
        this.config.options.sync = true;
    },

    // disable line indicating current pointer position within dataset
    disablePointerLine: function () {
        this.config.plugins[0].afterDatasetsDraw = () => {};
    },

    setDatasetBackgroundColours: function ({backgroundColour, hoverBackgroundColour} = {}) {
        this.config.data.datasets[0].backgroundColor = backgroundColour;
        this.config.data.datasets[0].hoverBackgroundColor = hoverBackgroundColour;
    },

    setTooltipLabel: function (callback) {
        this.config.options.tooltips.callbacks.label = callback;
    },

    setYAxisLabel: function (callback) {
        this.config.options.scales.yAxes[0].ticks.callback = callback;
    },

    setCanvasPadding: function (padding) {
        this.config.options.layout = {
            padding: padding,
        };
    },

    mergeXAxisTimeScale: function (timeConfig) {
        this.config.options.scales.xAxes[0].time = {...this.config.options.scales.xAxes[0].time, ...timeConfig};
    },

    addValues: function (values, getItemAxisValues) {
        values.map( function(item) {
            const {x, y} = getItemAxisValues(item);

            this.config.data.datasets[0].data.push({
                t: x,
                y,
            });
        }, this);
    },
};


export function syncTooltip(tooltip) {
    const dataPoints = tooltip.dataPoints || [];
    if (dataPoints.length < 1) {
        clearActiveChartPointers();

        return;
    }

    // assumes only one active data point at a time on a single chart
    const activeDataPoint = dataPoints[0];

    Chart.helpers.each(Chart.instances, function(instance){
        const syncChart = instance.config.options.sync || false;

        if (syncChart === false) {
            return;
        }

        if (chartActivePointMatches (instance, activeDataPoint)) {
            return;
        }

        const chartPoint = instance.getDatasetMeta(0).data[activeDataPoint.index].getCenterPoint();

        instance.canvas.dispatchEvent(
            new MouseEvent('mousemove', {
                // only x position needs to be simulated to get tooltip to display
                clientX: instance.canvas.getBoundingClientRect().left + chartPoint.x,
            })
        );
    });
}

function chartActivePointMatches (chartInstance, activeDataPoint) {
    if (chartInstance.active === undefined) {
        return false;
    }

    if (chartInstance.active.length < 1) {
        return false;
    }

    return chartInstance.active[0]._index === activeDataPoint.index;
}

function clearActiveChartPointers () {
    Chart.helpers.each(Chart.instances, function(instance) {
        const leaveEvent = new MouseEvent('mouseout');
        instance._listeners.mouseout(leaveEvent);
    });
}

export { chartConfig, chartConfigurator, ChartError, chartRange, ChartToolbar, defaultChartColours };

